// The Terrain Matrix, ported from the demo's Matrix (scrips-demo-samer `?view=terrain` → Matrix, synthetic patient): the IFM
// nodes and the lifestyle levers as rows, the seven body systems as columns. A cell counts the markers a node shares with a
// system (or the plan items a lever aims at a system). The record holds no IFM node per marker and no plan item by lever yet,
// so every cell is empty ("·") and every node reads "not on file": the grid is drawn, never filled with made-up counts.
import { cn } from '@/lib/utils';
import { V2_CARD_TITLE, V2_META, V2_META_STRONG } from '@/shared/ui/design-v2';
import { TERRAIN_SYSTEMS, type TerrainSystemId } from './lib/terrain-catalog';

/** The demo's IFM nodes, in its order. */
export const IFM_NODES = ['Assimilation', 'Defence', 'Energy', 'Biotransformation', 'Transport', 'Communication', 'Structure', 'Mind'] as const;
/** The demo's lifestyle levers, in its order. */
export const LIFESTYLE_LEVERS = ['Clinical', 'Behavioural / neuro', 'Supplements / Rx', 'Goals', 'Nutrition', 'Physical activity', 'Restorative sleep'] as const;

const SECONDARY = 'text-text-secondary';
// Compact so all 7 columns fit beside the Scrips Intelligence rail at 1440 (~318 px of card content): 24 px cells, 2 px apart,
// a label of at most 112 px plus the label cell's 8 px right padding. border-spacing adds 2 px around all 8 columns:
// 112 + 8 + 7 x 24 + 9 x 2 = 306 px.
const CELL = 'inline-flex size-6 items-center justify-center rounded-md border border-border-card text-text-muted';

/** Two-letter column codes: the DS icon set has a heart but no icon for the other six systems (the demo draws icons). The
 *  full name is the header's tooltip and accessible name. */
const COLUMN_CODE: Record<TerrainSystemId, string> = {
  'heart-vessels': 'HV',
  metabolism: 'Me',
  'inflammation-immunity': 'Im',
  'liver-kidney-blood': 'LK',
  hormones: 'Ho',
  'sleep-recovery-airway': 'Sl',
  'brain-nervous': 'Br',
};

function Row({ label, glyph, selected }: { label: string; glyph?: string; selected: TerrainSystemId | null }) {
  return (
    <tr>
      <th scope="row" className={cn(V2_META, 'py-0.5 pr-2 text-left font-normal text-text-primary')} title={label}>
        {/* The width cap and the ellipsis sit on block boxes inside the cell: a table cell ignores max-width. */}
        <span className="flex max-w-28 items-center gap-2">
          {glyph ? (
            <span aria-hidden className="text-text-muted">
              {glyph}
            </span>
          ) : null}
          <span className="min-w-0 truncate">{label}</span>
        </span>
      </th>
      {TERRAIN_SYSTEMS.map((s) => (
        <td key={s.id} className="p-0 text-center">
          <span
            className={cn(CELL, selected === s.id ? 'bg-blue-100' : 'bg-surface-card')}
            aria-label={`${label} × ${s.short} · not on file`}
            data-matrix-cell={`${label}|${s.id}`}
          >
            ·
          </span>
        </td>
      ))}
    </tr>
  );
}

export function TerrainMatrix({ selected }: { selected: TerrainSystemId | null }) {
  return (
    <div data-slot="terrain-matrix" className="flex flex-col gap-3">
      <header className="flex min-h-8 flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <h2 className={V2_CARD_TITLE}>IFM nodes not on file</h2>
        <span className={cn(V2_META, 'min-w-0 truncate', SECONDARY)}>markers shared by each node and system · plan items by lever</span>
      </header>
      <p className={cn(V2_META, SECONDARY)}>The record holds no IFM node per marker and no plan item by lever yet, so every cell is empty.</p>
      <div className="overflow-x-auto">
        <table className="border-separate border-spacing-0.5" aria-label="IFM nodes and lifestyle levers by body system">
          <thead>
            <tr>
              <th scope="col" className={cn(V2_META, 'py-0.5 pr-2 text-left font-normal', SECONDARY)}>
                IFM node
              </th>
              {TERRAIN_SYSTEMS.map((s) => (
                <th
                  key={s.id}
                  scope="col"
                  title={s.label}
                  aria-label={s.label}
                  className={cn(V2_META, 'w-6 px-0 pb-1 text-center font-normal', selected === s.id ? 'text-brand-primary' : SECONDARY)}
                >
                  {COLUMN_CODE[s.id]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {IFM_NODES.map((n) => (
              <Row key={n} label={n} glyph="◌" selected={selected} />
            ))}
            <tr>
              <th scope="rowgroup" colSpan={TERRAIN_SYSTEMS.length + 1} className={cn(V2_META, 'pt-3 pb-1 text-left font-normal', SECONDARY)}>
                Lifestyle lever
              </th>
            </tr>
            {LIFESTYLE_LEVERS.map((l) => (
              <Row key={l} label={l} selected={selected} />
            ))}
          </tbody>
        </table>
      </div>
      <p className={cn(V2_META, 'flex flex-wrap gap-x-4 gap-y-1', SECONDARY)} data-slot="terrain-matrix-legend">
        <span>
          <span aria-hidden className="text-text-muted">◌</span> Not on file or not yet read
        </span>
      </p>
      <span className={cn(V2_META_STRONG, 'sr-only')}>No IFM or lever data on the record.</span>
    </div>
  );
}
